export interface PdfExportConfigDialogProps {
  open: boolean;
  onClose: () => void;
}

// PDF Export Options
export enum PdfExportOption {
  VALUATIONS = 'valuations',
  OTHER = 'other',
  FUND = 'fund',
  FIRM = 'firm',
}

// Company sub-options
export interface CompanyPdfOptions {
  valuationSummary: boolean;
  equityAllocation: boolean;
  concludedShareValues: boolean;
  financials: boolean;
  capTable: boolean;
  fundOwnership: boolean;
  breakpointAnalysis: boolean;
  cashFlowLedger: boolean;
  approachBacksolve: boolean;
  approachDcf: boolean;
  approachExternalValuation: boolean;
  approachFutureExit: boolean;
  approachPublicComps: boolean;
  approachTransactionComps: boolean;
  approachSpecifiedShareValues: boolean;
  approachCalibration: boolean;
}

// Fund sub-options
export interface FundPdfOptions {
  summary: boolean;
  scheduleOfInvestments: boolean;
}

// Firm sub-options
export interface FirmPdfOptions {
  summary: boolean;
}

// Main PDF export configuration
export interface PdfExportConfiguration {
  selectedOption: PdfExportOption;
  companyOptions: CompanyPdfOptions;
  companyOptionsOrder: Array<keyof CompanyPdfOptions>;
  fundOptions: FundPdfOptions;
  firmOptions: FirmPdfOptions;
}

// Company option configurations for UI and processing (non–valuation-approach sections)
export const COMPANY_OPTION_CONFIGS: Array<{ key: keyof Pick<
  CompanyPdfOptions,
  | 'valuationSummary'
  | 'equityAllocation'
  | 'concludedShareValues'
  | 'financials'
  | 'capTable'
  | 'fundOwnership'
  | 'breakpointAnalysis'
  | 'cashFlowLedger'
>; label: string }> = [
  { key: 'valuationSummary', label: 'Valuation Summary' },
  { key: 'equityAllocation', label: 'Equity Allocation' },
  { key: 'concludedShareValues', label: 'Concluded Share Values' },
  { key: 'financials', label: 'Financials' },
  { key: 'capTable', label: 'Cap Table' },
  { key: 'breakpointAnalysis', label: 'Breakpoints' },
  { key: 'fundOwnership', label: 'Fund Ownership' },
  { key: 'cashFlowLedger', label: 'Cash Flow Ledger' },
];

export const VALUATION_APPROACH_OPTION_CONFIGS = [
  { key: 'approachBacksolve' as const, label: 'Backsolve' },
  { key: 'approachDcf' as const, label: 'Discounted Cash Flow' },
  { key: 'approachExternalValuation' as const, label: 'External Valuation' },
  { key: 'approachFutureExit' as const, label: 'Future Exit' },
  { key: 'approachPublicComps' as const, label: 'Public Comps' },
  { key: 'approachTransactionComps' as const, label: 'Transaction Comps' },
  { key: 'approachSpecifiedShareValues' as const, label: 'Specified Share Values' },
  { key: 'approachCalibration' as const, label: 'Calibration' },
] as const satisfies ReadonlyArray<{ key: keyof CompanyPdfOptions; label: string }>;

export type CompanyPdfApproachOptionKey = (typeof VALUATION_APPROACH_OPTION_CONFIGS)[number]['key'];

export const DEFAULT_COMPANY_OPTIONS_ORDER_NON_APPROACH: Array<keyof CompanyPdfOptions> =
  COMPANY_OPTION_CONFIGS.map(c => c.key);

export const DEFAULT_COMPANY_OPTIONS_ORDER: Array<keyof CompanyPdfOptions> = [
  ...DEFAULT_COMPANY_OPTIONS_ORDER_NON_APPROACH,
  ...VALUATION_APPROACH_OPTION_CONFIGS.map(c => c.key),
];

// Default configurations
export const DEFAULT_COMPANY_OPTIONS: CompanyPdfOptions = {
  valuationSummary: true,
  equityAllocation: true,
  concludedShareValues: true,
  financials: true,
  capTable: true,
  fundOwnership: true,
  breakpointAnalysis: true,
  cashFlowLedger: false,
  approachBacksolve: true,
  approachDcf: true,
  approachExternalValuation: true,
  approachFutureExit: true,
  approachPublicComps: true,
  approachTransactionComps: true,
  approachSpecifiedShareValues: true,
  approachCalibration: true,
};

export const DEFAULT_FUND_OPTIONS: FundPdfOptions = {
  summary: true,
  scheduleOfInvestments: true,
};

export const DEFAULT_FIRM_OPTIONS: FirmPdfOptions = {
  summary: true,
};

export const DEFAULT_PDF_CONFIGURATION: PdfExportConfiguration = {
  selectedOption: PdfExportOption.VALUATIONS,
  companyOptions: DEFAULT_COMPANY_OPTIONS,
  companyOptionsOrder: DEFAULT_COMPANY_OPTIONS_ORDER_NON_APPROACH,
  fundOptions: DEFAULT_FUND_OPTIONS,
  firmOptions: DEFAULT_FIRM_OPTIONS,
};
